import { useState, useCallback, useRef, useEffect } from 'react';
import { message } from 'antd';
import apiService from '../services/api';
import { ServiceType } from '../services/api';
import { StockInstrument } from '../types';

const coreService = apiService.getService(ServiceType.CORE);

interface UseStockSearchOptions {
    debounceDelay?: number;
    minSearchLength?: number;
}

export const useStockSearch = ({ 
    debounceDelay = 300,
    minSearchLength = 1 
}: UseStockSearchOptions = {}) => {
    const [searchResults, setSearchResults] = useState<StockInstrument[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, []);

    const handleSearch = useCallback((value: string) => {
        // Clear previous timer
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        if (!value || value.length < minSearchLength) {
            setSearchResults([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        // Debounce API call
        searchTimerRef.current = setTimeout(async () => {
            try {
                const response = await coreService.get(`/search/stocks?q=${encodeURIComponent(value)}`);

                // Transform API response to match StockInstrument interface
                const results: StockInstrument[] = response.data.results.map((item: any) => ({
                    symbol: item.symbol,
                    name: item.symbol, // API doesn't provide name, use symbol
                    price: item.price,
                    volume: 0, // API doesn't provide volume, set to 0
                    marketCap: 0, // API doesn't provide marketCap, set to 0
                    change24h: item.latestChange || 0,
                    changePercent24h: item.changePercent || 0,
                    proportion: 0 // Default proportion
                }));

                setSearchResults(results);
            } catch (error: any) {
                console.error('Error searching stocks:', error);
                message.error('Failed to search stocks');
                setSearchResults([]);
            } finally {
                setLoading(false);
            }
        }, debounceDelay);
    }, [debounceDelay, minSearchLength]);

    const clearResults = useCallback(() => {
        setSearchResults([]);
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
    }, []);

    return {
        searchResults,
        loading,
        handleSearch,
        clearResults
    };
};



